// Auth wrappers (Checkpoint 5.01 — Phase 1a). A thin, engine-facing layer over the Firebase modular
// SDK so the rest of the app never imports firebase/auth directly and never sees raw error codes.
// Tree-shakeable: we import ONLY the functions used, so the bundle carries just these auth paths.
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from './firebase.js';

// The core boot hook (Lane B): subscribe to auth state. onAuthStateChanged is ASYNC — its FIRST
// callback is the "initializing resolved" signal boot.js waits on before routing (authed → game /
// signed-out → splash). Stays live after: a sign-out fires it again with null, a sign-in with a user.
// Returns the unsubscribe fn (unused today; kept honest).
export function watchAuth(cb) {
  return onAuthStateChanged(auth, cb);
}

// Google sign-in via popup (Lane C). Provider enabled in the Firebase console (Peter's step).
export function googleSignIn() {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
}

// Email/password sign-up and sign-in (Lane C).
export function emailSignUp(email, password) {
  return createUserWithEmailAndPassword(auth, email, password);
}
export function emailSignIn(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

// Sign-out (Lane C — the in-game control). boot.js reloads to a fresh splash once auth clears.
export function doSignOut() {
  return signOut(auth);
}

// Map Firebase auth error codes → friendly, user-facing copy (Lane C). Users NEVER see a raw
// `auth/...` code. Unknown codes fall back to a calm generic line. Popup-closed / cancelled are
// deliberately treated as non-errors by the caller (splash.js) — nothing went wrong, the user
// just backed out — but a message is provided here in case a caller wants one.
const AUTH_MESSAGES = {
  'auth/invalid-email': 'That email address doesn’t look right.',
  'auth/missing-email': 'Please enter your email address.',
  'auth/missing-password': 'Please enter your password.',
  'auth/weak-password': 'Password is too weak — use at least 6 characters.',
  'auth/email-already-in-use': 'That email is already registered — try signing in instead.',
  'auth/user-not-found': 'No account found for that email — sign up to create one.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/invalid-login-credentials': 'Incorrect email or password.',
  'auth/too-many-requests': 'Too many attempts — please wait a moment and try again.',
  'auth/network-request-failed': 'Network problem — check your connection and try again.',
  'auth/popup-blocked': 'Your browser blocked the sign-in popup — allow popups and try again.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  'auth/operation-not-allowed': 'This sign-in method isn’t enabled yet.',
  'auth/user-disabled': 'This account has been disabled.',
};

export function friendlyAuthError(err) {
  const code = err && err.code ? err.code : '';
  return AUTH_MESSAGES[code] || 'Something went wrong signing in — please try again.';
}

// True for the "user backed out of the popup" cases — the caller should stay silent (not an error).
export function isCancellation(err) {
  const code = err && err.code ? err.code : '';
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
}
